<script lang="ts">
  import WalletPay from './wallet-pay/WalletPay.svelte'
  import { onMount } from 'svelte'
  import { get } from 'svelte/store'
  import { stripeStyles } from '@pages/payment/stripe/Stripe.styles'
  import { dispatch, PassportEvents } from '@lib/events/events'
  import { loadScript } from '@lib/scripts/scripts'
  import { pageOptsStore, pageStore, fn } from '@packages/stores'
  import { Pages } from '@packages/utils/enums'
  import { Stripe as StripeComponent } from '@packages/ui'
  import { configStore } from '@src/stores/config'
  import { getClientConfigByClientId } from '@lib/clients'
  import type { TPaymentAsset } from '../payment.constants'
  import { afterPaymentProcess } from '../Payment.service'
  import { createOrder, processOrder } from '../order.service'
  import { getOrderDetails } from '@lib/orders'

  export let priceCents: number
  export let isLoading: boolean
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  export let purchaseConfig: { assets: TPaymentAsset[]; title: string; subtitle: string }
  export let isPriceLoading: boolean
  export let feeToApply: number = 0

  const { toastAlert } = get(fn)
  const config = get(configStore)

  let Stripe: Window['Stripe']
  let stripe: Window['Stripe']
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let stripeElements: any
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let cardElement: any
  let isButtonActive: boolean

  let isCardFocused = false
  onMount(async () => {
    try {
      if (!Stripe) {
        if (!window.Stripe) {
          await loadScript('https://js.stripe.com/v3/')
          if (!window.Stripe) throw {}
        }
        Stripe = window.Stripe
      }
    } catch (err) {
      return console.log('Please include stripe to use payments.')
    }
    if (stripe) return
    const clientName = getClientConfigByClientId(config.clientId).name
    stripe = Stripe(config.stripe.pk[clientName] ?? config.stripe.pk.DEFAULT)
    stripeElements = stripe.elements()
    cardElement = stripeElements.create('card', { style: stripeStyles })
    cardElement.mount('#cpui-card-element')
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    cardElement.on('change', (event: any) => {
      isButtonActive = event.complete && !event.error
    })
    cardElement.on('focus', () => (isCardFocused = true))
    cardElement.on('blur', () => (isCardFocused = false))
  })

  const handleSubmit = async () => {
    if (!isButtonActive) return
    await doPayment()
  }

  const externalErrorHandler = (err: Error) => {
    isLoading = false
    toastAlert(`Payment error: ${err.message || err}`, 'failure')
    pageOptsStore.set({ errorMessage: err.message })
    pageStore.set(Pages.ERROR)
    dispatch(PassportEvents.ERROR, { error: err })
  }

  const doPayment = async (paymentMethodId?: string) => {
    try {
      const { assets } = purchaseConfig
      const config = get(configStore)
      if (!assets?.length) throw new Error('No purchase config assets found')
      isLoading = true

      const orderId = await createOrder({
        assets,
        payment_provider: 'stripe',
        client_id: config.clientId,
        currency: 'USD',
      })
      const order = await getOrderDetails(config.credenza.generalApiUrl, orderId)
      const clientSecret = order.payment_provider_data?.client_secret
      if (!clientSecret) throw new Error('Payment is unavailable now')

      const result = await stripe.confirmCardPayment(clientSecret, {
        payment_method: paymentMethodId || {
          card: cardElement,
        },
      })
      if (result.error) throw result.error

      const res = await processOrder(orderId)
      await afterPaymentProcess({ res, priceCents, purchaseConfig })
    } catch (err) {
      externalErrorHandler(err)
    } finally {
      isLoading = false
    }
  }

  $: isPaymentDisabled = !isButtonActive || isLoading || isPriceLoading
  $: totalToPay = (Math.ceil(priceCents) / 100).toFixed(2)
</script>

<StripeComponent
  bind:isPaymentDisabled
  bind:isLoading
  bind:totalToPay
  options={{
    isCardFocused,
    onPay: handleSubmit,
    feeToApply,
  }}
/>

{#if stripe && stripeElements}
  <WalletPay {doPayment} {stripe} {stripeElements} {priceCents} bind:isLoading />
{/if}
